import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Form,
  Input,
  Label,
  Modal,
  Pagination,
  Select,
  Table,
  TextArea,
} from 'semantic-ui-react';
import { API, showError, showSuccess } from '../helpers';
import { ITEMS_PER_PAGE } from '../constants';

const CHANNEL_OPTIONS = [
  { key: 'all', text: '全部渠道', value: '' },
  { key: 'oa', text: '公众号模板消息', value: 'oa' },
  { key: 'sms', text: '短信', value: 'sms' },
];

const CHANNEL_FORM_OPTIONS = [
  { key: 'oa', text: '公众号模板消息（oa）', value: 'oa' },
  { key: 'sms', text: '短信（sms）', value: 'sms' },
];

const FIELD_MAP_HINT = {
  oa: '{"data":{"first":"$title","keyword1":"name","remark":"remark"}}（键为微信模板 data 字段名，值为调用方参数键；$title / $content 指向请求体同名字段）',
  sms: '{"params":["name","time"]}（阿里云模板变量顺序；对象入参按键名取值，数组入参按此顺序映射）',
};

const emptyForm = {
  id: 0,
  channel: 'oa',
  template_key: '',
  platform_template_id: '',
  content: '',
  field_map: '',
  status: 1,
  remark: '',
};

const MessageTemplatesTable = () => {
  const [templates, setTemplates] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [channel, setChannel] = useState('');
  const [keyword, setKeyword] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const loadTemplates = async (page = 1, ch = channel, kw = keyword) => {
    setLoading(true);
    let url = `/api/message/template/?p=${page - 1}`;
    if (ch) {
      url += `&channel=${ch}`;
    }
    if (kw) {
      url += `&keyword=${encodeURIComponent(kw)}`;
    }
    const res = await API.get(url);
    const { success, message, data, total: count } = res.data;
    if (success) {
      setTemplates(data || []);
      setTotal(count || 0);
      setActivePage(page);
    } else {
      showError(message);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadTemplates(1).catch((reason) => showError(reason));
  }, []);

  const openEdit = (template) => {
    setForm({
      id: template.id,
      channel: template.channel,
      template_key: template.template_key,
      platform_template_id: template.platform_template_id || '',
      content: template.content || '',
      field_map: template.field_map || '',
      status: template.status,
      remark: template.remark || '',
    });
    setModalOpen(true);
  };

  const submit = async () => {
    if (!form.template_key) {
      showError('template_key 不能为空');
      return;
    }
    setSaving(true);
    const payload = { ...form };
    const res = form.id
      ? await API.put('/api/message/template/', payload)
      : await API.post('/api/message/template/', payload);
    const { success, message } = res.data;
    if (success) {
      showSuccess(message || '保存成功');
      setModalOpen(false);
      await loadTemplates(activePage);
    } else {
      showError(message);
    }
    setSaving(false);
  };

  const toggleStatus = async (template) => {
    const res = await API.put('/api/message/template/', {
      id: template.id,
      channel: template.channel,
      template_key: template.template_key,
      platform_template_id: template.platform_template_id,
      content: template.content,
      field_map: template.field_map,
      status: template.status === 1 ? 0 : 1,
      remark: template.remark,
    });
    const { success, message } = res.data;
    if (success) {
      showSuccess('已更新');
      await loadTemplates(activePage);
    } else {
      showError(message);
    }
  };

  const remove = async (template) => {
    if (!window.confirm(`确定删除模板 ${template.template_key} 吗？`)) {
      return;
    }
    const res = await API.delete(`/api/message/template/${template.id}`);
    const { success, message } = res.data;
    if (success) {
      showSuccess(message || '删除成功');
      await loadTemplates(activePage);
    } else {
      showError(message);
    }
  };

  return (
    <>
      <Form>
        <Form.Group>
          <Form.Field width={4}>
            <Select
              options={CHANNEL_OPTIONS}
              value={channel}
              onChange={(e, { value }) => {
                setChannel(value);
                loadTemplates(1, value, keyword).catch((reason) => showError(reason));
              }}
            />
          </Form.Field>
          <Form.Field width={6}>
            <Input
              placeholder='按 template_key / 平台模板 ID / 备注搜索'
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  loadTemplates(1).catch((reason) => showError(reason));
                }
              }}
            />
          </Form.Field>
          <Button
            type='button'
            loading={loading}
            onClick={() => loadTemplates(1).catch((reason) => showError(reason))}
          >
            查询
          </Button>
          <Button
            type='button'
            color='green'
            onClick={() => {
              setForm({ ...emptyForm, channel: channel || 'oa' });
              setModalOpen(true);
            }}
          >
            新增模板
          </Button>
        </Form.Group>
      </Form>
      <Table basic={'very'} compact size='small'>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>ID</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>渠道</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>业务标识</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>平台模板 ID</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>模板原文</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>字段映射</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>状态</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>备注</Table.HeaderCell>
            <Table.HeaderCell style={{ whiteSpace: 'nowrap' }}>操作</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {templates.map((template) => (
            <Table.Row key={template.id}>
              <Table.Cell>{template.id}</Table.Cell>
              <Table.Cell>{template.channel_text || template.channel}</Table.Cell>
              <Table.Cell>
                <code style={{ fontSize: '12px' }}>{template.template_key}</code>
              </Table.Cell>
              <Table.Cell>
                <code style={{ fontSize: '12px' }}>
                  {template.platform_template_id || '未配置'}
                </code>
              </Table.Cell>
              <Table.Cell style={{ maxWidth: 260 }}>
                <div style={{ fontSize: '12px', wordBreak: 'break-all' }}>
                  {template.content || '-'}
                </div>
              </Table.Cell>
              <Table.Cell style={{ maxWidth: 320 }}>
                <div style={{ fontSize: '12px', wordBreak: 'break-all', color: template.field_map_valid ? '#5a6b7f' : '#c0392b' }}>
                  {template.field_map || '未配置'}
                  {template.field_map_valid ? '' : '（JSON 非法）'}
                </div>
              </Table.Cell>
              <Table.Cell>
                <Label basic color={template.status === 1 ? 'green' : 'grey'}>
                  {template.status === 1 ? '启用' : '禁用'}
                </Label>
              </Table.Cell>
              <Table.Cell>{template.remark || '-'}</Table.Cell>
              <Table.Cell style={{ whiteSpace: 'nowrap' }}>
                <Button size='mini' basic onClick={() => openEdit(template)}>
                  编辑
                </Button>
                <Button size='mini' basic onClick={() => toggleStatus(template)}>
                  {template.status === 1 ? '禁用' : '启用'}
                </Button>
                <Button size='mini' basic color='red' onClick={() => remove(template)}>
                  删除
                </Button>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan='9'>
              <Pagination
                floated='right'
                activePage={activePage}
                onPageChange={(e, { activePage: page }) =>
                  loadTemplates(page).catch((reason) => showError(reason))
                }
                size='small'
                siblingRange={1}
                totalPages={Math.max(1, Math.ceil(total / ITEMS_PER_PAGE))}
              />
              <span style={{ color: '#8a919f' }}>
                共 {total} 条模板（全局通用，所有接入系统共用同一套 template_key 映射）
              </span>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} size='large'>
        <Modal.Header>{form.id ? '编辑消息模板' : '新增消息模板'}</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Group widths='equal'>
              <Form.Field>
                <label>渠道</label>
                <Select
                  options={CHANNEL_FORM_OPTIONS}
                  value={form.channel}
                  onChange={(e, { value }) => setForm({ ...form, channel: value })}
                />
              </Form.Field>
              <Form.Field>
                <label>业务标识（template_key，跨系统统一命名）</label>
                <Input
                  placeholder='如 hr_interview_notice'
                  value={form.template_key}
                  onChange={(e) => setForm({ ...form, template_key: e.target.value })}
                />
              </Form.Field>
              <Form.Field>
                <label>状态</label>
                <Checkbox
                  toggle
                  checked={form.status === 1}
                  label={form.status === 1 ? '启用' : '禁用'}
                  onChange={(e, { checked }) =>
                    setForm({ ...form, status: checked ? 1 : 0 })
                  }
                />
              </Form.Field>
            </Form.Group>
            <Form.Field>
              <label>
                平台模板 ID
                {form.channel === 'oa' ? '（微信模板 ID）' : '（阿里云 TemplateCode，报备通过后填写）'}
              </label>
              <Input
                placeholder={form.channel === 'oa' ? '微信模板 ID' : '如 SMS_123456789'}
                value={form.platform_template_id}
                onChange={(e) =>
                  setForm({ ...form, platform_template_id: e.target.value })
                }
              />
            </Form.Field>
            <Form.Field>
              <label>模板原文（可选，由业务系统提供，用于双方核对与向阿里云报备）</label>
              <TextArea
                rows={2}
                placeholder='如：【招聘系统】{name}，您的面试安排为：{position}，时间 {time}。'
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
              />
            </Form.Field>
            <Form.Field>
              <label>字段映射 field_map（JSON）</label>
              <TextArea
                rows={3}
                placeholder={FIELD_MAP_HINT[form.channel]}
                value={form.field_map}
                onChange={(e) => setForm({ ...form, field_map: e.target.value })}
              />
              <div style={{ color: '#8a919f', fontSize: '12px', marginTop: 6 }}>
                {FIELD_MAP_HINT[form.channel]}
              </div>
            </Form.Field>
            <Form.Field>
              <label>备注</label>
              <Input
                placeholder='用途说明，便于后续维护'
                value={form.remark}
                onChange={(e) => setForm({ ...form, remark: e.target.value })}
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setModalOpen(false)}>取消</Button>
          <Button color='green' loading={saving} onClick={submit}>
            保存
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default MessageTemplatesTable;
